import { actAsStore, ensureAccessToken } from '../api/client.js';

export interface TraceStep {
  name: string;
  args: unknown;
  failed: boolean;
}

/** What the server sends while a question runs; see assistantRoutes.ts. */
export type AskStreamEvent =
  | { type: 'text'; delta: string }
  | { type: 'tool'; phase: 'start'; id: string; name: string; args: unknown }
  | { type: 'tool'; phase: 'end'; id: string; name: string; failed: boolean; ms: number }
  | {
      type: 'done';
      text: string;
      trace: TraceStep[];
      model: string;
      exhausted: boolean;
      conversation: { id: string; title: string } | null;
    }
  | { type: 'error'; message: string };

/**
 * Splits a server-sent event stream into events, across chunk boundaries.
 *
 * Only `data:` lines are read; comment lines (the server's heartbeat) and any
 * other field are skipped. A block that is not valid JSON is dropped rather
 * than ending the stream, since one bad event should not cost the answer.
 */
export class EventParser {
  private buffer = '';

  push(chunk: string): AskStreamEvent[] {
    this.buffer += chunk.replace(/\r\n/g, '\n');
    const out: AskStreamEvent[] = [];
    let end: number;
    while ((end = this.buffer.indexOf('\n\n')) !== -1) {
      const block = this.buffer.slice(0, end);
      this.buffer = this.buffer.slice(end + 2);
      const data = block
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).replace(/^ /, ''))
        .join('\n');
      if (!data) continue;
      try {
        out.push(JSON.parse(data) as AskStreamEvent);
      } catch {
        // skipped, see above
      }
    }
    return out;
  }
}

/**
 * Asks a question and reports the answer as it is produced.
 *
 * fetch rather than axios, because axios in the browser hands over the body
 * only once it is complete — which is exactly the wait this exists to remove.
 * The token is refreshed up front for the same reason the live socket does:
 * there is no 401 interceptor here to retry a stale one.
 */
export async function streamAsk(
  url: string,
  body: { conversationId?: string; question: string },
  onEvent: (e: AskStreamEvent) => void,
): Promise<void> {
  const token = await ensureAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (actAsStore.id) headers['x-ro-act-as'] = actAsStore.id;

  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!res.ok || !res.body) {
    // Refused before the stream opened: the API's ordinary JSON error.
    const err = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw Object.assign(new Error(err?.error?.message ?? `HTTP ${res.status}`), { status: res.status });
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const parser = new EventParser();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    for (const e of parser.push(decoder.decode(value, { stream: true }))) onEvent(e);
  }
  for (const e of parser.push(decoder.decode() + '\n\n')) onEvent(e);
}
